import type { TutorPaymentPolicy } from '@entities/tutor'

import { TutorCategoryType } from '@entities/tutor'

import { usePaymentPoliciesStore } from './store'

interface SectionPaymentPolicyReturn {
  refHasChanges: ComputedRef<boolean>
  submitChanges: () => void
}

export const useSectionPaymentPolicy = (): SectionPaymentPolicyReturn => {
  const paymentPoliciesStore = usePaymentPoliciesStore()

  // ── Initial sync: tutorPolicies → paymentPolicies.selected* ───
  // Saved policies arrive asynchronously (middleware fire-and-forget).
  // First non-empty arrival seeds the form-state fields. After that the
  // user controls them through the widgets — no further auto-sync.
  let synced = false

  const syncFromTutorPolicies = (policies: TutorPaymentPolicy[]): void => {
    if (synced) return
    if (policies.length === 0) return
    synced = true

    for (const policy of policies) {
      switch (policy.category) {
        case TutorCategoryType.DemoMath:
          paymentPoliciesStore.setSelectedDemoMath(policy.id)
          break
        case TutorCategoryType.DemoEla:
          paymentPoliciesStore.setSelectedDemoEla(policy.id)
          break
        case TutorCategoryType.PaidMath:
          paymentPoliciesStore.setSelectedPaidMath(policy.id)
          break
        case TutorCategoryType.PaidEla:
          paymentPoliciesStore.setSelectedPaidEla(policy.id)
          break
      }
    }
  }

  watch(
    () => paymentPoliciesStore.getTutorPolicies,
    syncFromTutorPolicies,
    { immediate: true },
  )

  // ── Save flow (existing — out of scope for this task) ──────────
  // refHasChanges still reads editPolicies; will not activate after
  // widgets stopped calling setValue. See follow-up task.
  const refHasChanges = computed<boolean>(
    () => paymentPoliciesStore.hasPolicyChanges,
  )

  const submitChanges = (): void => {
    if (!paymentPoliciesStore.hasPolicyChanges) return
    paymentPoliciesStore.setEdits(paymentPoliciesStore.getDraftEdits)
    paymentPoliciesStore.setModalConfirmPaymentPolicy(true)
  }

  return {
    refHasChanges,
    submitChanges,
  }
}
